import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import '@fontsource/barlow-condensed/700.css';
import '@fontsource/barlow-condensed/800.css';
import '@fontsource/barlow-condensed/800-italic.css';
import { api, endpoints } from '../../config/api';
import { Button } from '../../components/ui';
import useBrandLogo from '../../hooks/useBrandLogo';
import { useT, apiErrorMessage } from '../../i18n';
import LanguageSwitcher from '../../components/LanguageSwitcher/LanguageSwitcher';
import { InkBackdrop } from '../marketing/landing/InkBackdrop';
import { Cursor, EASE_OUT, MaskedHeading } from '../marketing/landing/primitives';
import './LoginPage.css';

export const MicrosoftLogo = () => (
  <svg width="18" height="18" viewBox="0 0 23 23" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <rect width="11" height="11" fill="#f25022" />
    <rect x="12" width="11" height="11" fill="#7fba00" />
    <rect y="12" width="11" height="11" fill="#00a4ef" />
    <rect x="12" y="12" width="11" height="11" fill="#ffb900" />
  </svg>
);

// On phones the shape sits small, above the card, instead of behind it.
const LOGIN_PORTRAIT = { y: 0.66, fit: 0.42 };

const messages = {
  trust: {
    tr: ['Şifre yok — Microsoft ile güvenli giriş', 'Her kurumun verisi izole', 'KVKK’ya uygun'],
    en: ['No passwords — secure Microsoft sign-in', 'Every organization’s data isolated', 'KVKK compliant'],
  },
  msError: { tr: 'Microsoft ile giriş şu anda başlatılamadı', en: 'Microsoft sign-in couldn’t be started right now' },
  homeAria: { tr: 'Martinci İmza anasayfa', en: 'Martinci home' },
  home: { tr: '← Anasayfa', en: '← Home' },
  kicker: { tr: 'İmza Yönetim Portalı', en: 'Signature Manager' },
  titleLines: {
    tr: ['Tekrar', [{ text: 'hoş geldiniz.', className: 'lp-accent' }]],
    en: ['Welcome', [{ text: 'back.', className: 'lp-accent' }]],
  },
  lead: {
    tr: 'Kurumsal Microsoft hesabınızla giriş yapın. Şirket e-posta adresinizle ilk kez giriyorsanız hesabınız otomatik oluşturulur.',
    en: 'Sign in with your company Microsoft account. If this is your first time signing in with your work email, your account is created automatically.',
  },
  redirecting: { tr: 'Microsoft’a yönlendiriliyor…', en: 'Redirecting to Microsoft…' },
  signIn: { tr: 'Microsoft ile Giriş Yap', en: 'Sign in with Microsoft' },
  terms: {
    tr: () => <>Giriş yaparak <Link to="/kullanim-sartlari">Kullanım Şartları</Link>’nı ve{' '}<Link to="/kvkk-aydinlatma-metni">KVKK Aydınlatma Metni</Link>’ni okuduğunuzu ve kabul ettiğinizi onaylarsınız.</>,
    en: () => <>By signing in you confirm that you have read and accept the <Link to="/kullanim-sartlari">Terms of Use</Link> and the{' '}<Link to="/kvkk-aydinlatma-metni">KVKK privacy notice</Link>.</>,
  },
  notCustomer: { tr: 'Kurumunuz henüz Martinci kullanmıyor mu?', en: 'Your organization isn’t on Martinci yet?' },
  startTrial: { tr: '14 gün ücretsiz deneyin →', en: 'Try it free for 14 days →' },
  legalAria: { tr: 'Yasal', en: 'Legal' },
  legalLinks: {
    tr: [['/kvkk-aydinlatma-metni', 'KVKK'], ['/gizlilik-politikasi', 'Gizlilik'], ['/cerez-politikasi', 'Çerezler']],
    en: [['/kvkk-aydinlatma-metni', 'KVKK Notice'], ['/gizlilik-politikasi', 'Privacy'], ['/cerez-politikasi', 'Cookies']],
  },
};

export const LoginPage = () => {
  const [error, setError] = useState('');
  const [msRedirecting, setMsRedirecting] = useState(false);
  const { t, lang } = useT(messages);
  const logo = useBrandLogo('white');

  const handleMicrosoftLogin = async () => {
    setError('');
    setMsRedirecting(true);
    try {
      const res = await api.get(endpoints.auth.ssoMicrosoftLoginUrl);
      window.location.href = res.authorize_url;
    } catch (err) {
      setError(apiErrorMessage(err, messages.msError));
      setMsRedirecting(false);
    }
  };

  return (
    <div className="auth" lang={lang}>
      <Cursor />
      <div className="lp-grain" aria-hidden="true" />
      {/* "@" at rest; folds into an envelope while we hand off to Microsoft. */}
      <InkBackdrop morph={msRedirecting ? 3 : 2} portrait={LOGIN_PORTRAIT} />

      <header className="auth-top">
        <Link to="/" className="auth-brand" aria-label={t('homeAria')}>
          <img src={logo.src} alt={logo.alt} />
        </Link>
        <div className="auth-top-actions">
          <LanguageSwitcher />
          <Link to="/" className="auth-back">{t('home')}</Link>
        </div>
      </header>

      <main className="auth-main">
        <motion.section
          className="auth-card"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE_OUT, delay: 0.15 }}
        >
          <span className="auth-kicker">{t('kicker')}</span>
          <MaskedHeading
            as="h1"
            immediate
            delay={0.3}
            className="auth-title"
            key={lang}
            lines={t('titleLines')}
          />
          <p className="auth-lead">{t('lead')}</p>

          {error && (
            <div className="auth-error" role="alert">
              <p>{error}</p>
            </div>
          )}

          <Button
            type="button"
            variant="microsoft"
            className="auth-ms-btn"
            onClick={handleMicrosoftLogin}
            disabled={msRedirecting}
          >
            <MicrosoftLogo />
            {msRedirecting ? t('redirecting') : t('signIn')}
          </Button>

          <p className="auth-terms">{t('terms')}</p>

          <div className="auth-demo">
            <span>{t('notCustomer')}</span>
            <Link to="/basla">{t('startTrial')}</Link>
          </div>
        </motion.section>

        <motion.ul
          className="auth-trust"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.9 }}
        >
          {t('trust').map((item) => <li key={item}>{item}</li>)}
        </motion.ul>
      </main>

      <footer className="auth-foot">
        <span>© {new Date().getFullYear()} Martinci Software &amp; Consulting</span>
        <nav aria-label={t('legalAria')}>
          {t('legalLinks').map(([to, label]) => <Link key={to} to={to}>{label}</Link>)}
        </nav>
      </footer>
    </div>
  );
};

export default LoginPage;
